import { API } from 'aws-amplify';
import { anonymize } from './anonymizer.util';
import { DemoModeService } from './demo-mode.service';
import { ViewAsScopeService } from '../view-as-scope';

type ApiMethod = (apiName: string, path: string, init?: unknown) => Promise<unknown>;

function wrapApiMethod(
  original: ApiMethod,
  demoModeService: DemoModeService,
  viewAsScopeService: ViewAsScopeService
): ApiMethod {
  return async (apiName: string, path: string, init?: unknown) => {
    const response = await original.call(API, apiName, path, init);
    const userAttr = viewAsScopeService.getOriginalUserAttributes();
    const isSuperAdmin = userAttr && userAttr['custom:role'] === 'sadmin';
    if (demoModeService.isDemoMode() && isSuperAdmin && response != null) {
      const queryParams = (init && typeof init === 'object' && (init as Record<string, unknown>).queryStringParameters) as Record<string, unknown> | undefined;
      let breakdownBy = queryParams && (queryParams.breakdownBy as string);
      if (!breakdownBy && typeof path === 'string') {
        // Drink breakdown API: labels are drink names, don't anonymize
        if (path.includes('drink-volume-breakdown') && !path.includes('drink-volume-machine-breakdown')) {
          breakdownBy = 'drink';
        }
        // Machine drink volume APIs delete breakdownBy but labels are always machine IDs
        else if (path.includes('drink-volume-by-machine') || path.includes('drink-volume-machine-breakdown')) {
          breakdownBy = 'machine';
        }
      }
      return anonymize(response, { breakdownBy });
    }
    return response;
  };
}

/**
 * Patches the Amplify API to anonymize responses when demo mode is enabled.
 * Only applies anonymization for super admins.
 * Must be called after Amplify.configure() in app initialization.
 */
export function patchAmplifyApiForDemoMode(
  demoModeService: DemoModeService,
  viewAsScopeService: ViewAsScopeService
): void {
  const originalGet = API.get.bind(API);
  const originalPost = API.post.bind(API);
  const originalPut = API.put.bind(API);
  const originalDel = API.del.bind(API);

  (API as { get: ApiMethod }).get = wrapApiMethod(originalGet as ApiMethod, demoModeService, viewAsScopeService);
  (API as { post: ApiMethod }).post = wrapApiMethod(originalPost as ApiMethod, demoModeService, viewAsScopeService);
  (API as { put: ApiMethod }).put = wrapApiMethod(originalPut as ApiMethod, demoModeService, viewAsScopeService);
  (API as { del: ApiMethod }).del = wrapApiMethod(originalDel as ApiMethod, demoModeService, viewAsScopeService);
}
